import { useEffect, useMemo, useState } from "react";
import type { User } from "firebase/auth";
import { collection, doc, onSnapshot, query, serverTimestamp, setDoc, where } from "firebase/firestore";
import { db } from "../firebase";
import type { Problem } from "../data/prep/topics";
import { buildPlan, goalItemId, paceFrom, type Plan, type PlanItem, type PrepEvent } from "./prepPlanner";
import { toApplications, type ApiItem } from "./applicationCards";
import { debriefEvents, oaEvents, type PrepEventDoc } from "./prepEvents";
import { COMPANY_DEBRIEFS, crowdFor, type CompanyDebrief, type Crowd } from "./companyDebriefs";
import { normalizeCompany } from "./dreamCompanies";
import { skillsFrom, type Skills } from "./prepSkills";
import { toPlanGoal, type CareerGoal } from "./goals";
import {
  pinnedFrom,
  practiceLog,
  shownDay,
  skillEvents,
  type PrepItemState,
  type PrepResult,
  type PrepSettingsDoc,
} from "./prep";

// The user's prep plan, live from Firestore: what Interview Prep shows and
// what Home's "Today's prep" to-do counts. Freezes today's list the first time
// either one works it out (users/{uid}/prepDays/{today}), so it's the same list
// in both places and doesn't change under the user.

/** What loads before the plan can be made */
const SOURCES = ["settings", "results", "items", "goals", "days", "cards", "events"];

/** Days planned ahead of today (how far the week arrows go) */
export const HORIZON = 35;

/** yyyy-mm-dd in the user's time zone */
export const localToday = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
};

/** Firestore rejects undefined fields */
export const plain = <T,>(value: T): T => JSON.parse(JSON.stringify(value));

export type PrepPlan = {
  today: string;
  /** Settings, results, items, goals and days have all loaded once */
  loaded: boolean;
  /** null: setup isn't done */
  settings: PrepSettingsDoc | null;
  /** The problem bank, loaded once setup is done */
  problems: Problem[] | null;
  results: PrepResult[];
  /** What the user did with items, as saved */
  states: PrepItemState[];
  /** By item id, with goal steps done on their goal counted as done */
  stateById: Map<string, PrepItemState>;
  goals: CareerGoal[];
  plan: Plan | null;
  /** A day's items: planned from today on, as shown before that */
  itemsOn: (day: string) => PlanItem[];
  /** The day an open goal step is planned for, from today on */
  plannedDay: Map<string, string>;
  /** The user's (unarchived) application cards */
  cards: ApiItem[];
  /** What the user said about each OA, by card id */
  eventDocs: Record<string, PrepEventDoc>;
  /** The OAs being prepped for, soonest first */
  events: PrepEvent[];
  /** Topic scores now; null before setup */
  skills: Skills | null;
  /** What earlier candidates said about each OA, by card id */
  crowds: Map<string, Crowd>;
};

export function usePrepPlan(user: User | null): PrepPlan {
  const [settings, setSettings] = useState<PrepSettingsDoc | null>(null);
  // What's still loading, and for whom: a sign-in (or a new user) starts over,
  // so nothing counts as loaded until that user's data is in
  const [loading, setLoading] = useState<{ uid: string | null; left: Set<string> }>({ uid: null, left: new Set(SOURCES) });
  const [results, setResults] = useState<PrepResult[]>([]);
  const [states, setStates] = useState<PrepItemState[]>([]);
  const [frozen, setFrozen] = useState<Record<string, PlanItem[]>>({});
  const [problems, setProblems] = useState<Problem[] | null>(null);
  const [goals, setGoals] = useState<CareerGoal[]>([]);
  const [cards, setCards] = useState<ApiItem[]>([]);
  const [eventDocs, setEventDocs] = useState<Record<string, PrepEventDoc>>({});
  const [today, setToday] = useState(localToday);

  // A new day while the page is open
  useEffect(() => {
    const tick = window.setInterval(() => setToday(localToday()), 60000);
    return () => window.clearInterval(tick);
  }, []);

  useEffect(() => {
    if (!user) {
      setSettings(null);
      setLoading({ uid: null, left: new Set() });
      return;
    }
    setLoading({ uid: user.uid, left: new Set(SOURCES) });
    const base = ["users", user.uid] as const;
    const done = (what: string) =>
      setLoading((state) => {
        if (state.uid !== user.uid || !state.left.has(what)) return state;
        const left = new Set(state.left);
        left.delete(what);
        return { uid: state.uid, left };
      });
    const fail = (what: string) => (error: unknown) => {
      console.error(`Could not load prep ${what}:`, error);
      done(what);
    };
    const unsubscribes = [
      onSnapshot(
        doc(db, ...base, "prep", "settings"),
        (snap) => {
          setSettings(snap.exists() ? (snap.data() as PrepSettingsDoc) : null);
          done("settings");
        },
        fail("settings")
      ),
      onSnapshot(
        collection(db, ...base, "prepResults"),
        (snap) => {
          setResults(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as PrepResult));
          done("results");
        },
        fail("results")
      ),
      onSnapshot(
        collection(db, ...base, "prepItems"),
        (snap) => {
          setStates(snap.docs.map((d) => d.data() as PrepItemState));
          done("items");
        },
        fail("items")
      ),
      onSnapshot(
        collection(db, ...base, "careerGoals"),
        (snap) => {
          setGoals(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as CareerGoal));
          done("goals");
        },
        fail("goals")
      ),
      // The same query as Home's, so the two share one listener
      onSnapshot(
        query(collection(db, ...base, "emails"), where("archiveId", "==", null)),
        (snap) => {
          setCards(toApplications(snap.docs));
          done("cards");
        },
        fail("cards")
      ),
      onSnapshot(
        collection(db, ...base, "prepEvents"),
        (snap) => {
          setEventDocs(Object.fromEntries(snap.docs.map((d) => [d.id, d.data() as PrepEventDoc])));
          done("events");
        },
        fail("events")
      ),
      onSnapshot(
        collection(db, ...base, "prepDays"),
        (snap) => {
          setFrozen(Object.fromEntries(snap.docs.map((d) => [d.id, (d.data().items || []) as PlanItem[]])));
          done("days");
        },
        fail("days")
      ),
    ];
    return () => unsubscribes.forEach((u) => u());
  }, [user]);

  // The problem bank is big, so it loads only once there's a plan to make
  const hasSettings = Boolean(settings);
  useEffect(() => {
    if (!hasSettings || problems) return;
    import("../data/prep/problems.json").then((m) => setProblems(m.default as Problem[]));
  }, [hasSettings, problems]);

  const loaded = loading.uid === (user?.uid ?? null) && loading.left.size === 0;

  // Every goal step's item id; a goal item not in here was deleted or edited away
  const goalStepIds = useMemo(
    () => new Set(goals.flatMap((g) => (g.lessons || []).map((step) => goalItemId(g.id, step.id)))),
    [goals]
  );
  const gone = (item: PlanItem) => item.kind === "goal" && loaded && !goalStepIds.has(item.id);

  // A goal step's checkbox lives on the goal, so it's done wherever it was checked off
  const stateById = useMemo(() => {
    const map = new Map(states.map((s) => [s.item.id, s]));
    for (const goal of goals) {
      for (const step of goal.lessons || []) {
        const id = goalItemId(goal.id, step.id);
        const saved = map.get(id);
        if (step.completed && saved?.status !== "done") {
          map.set(id, {
            ...(saved ?? { item: { id, kind: "goal", title: step.title, minutes: 0, reason: "" }, day: "" }),
            status: "done",
          });
        } else if (!step.completed && saved?.status === "done") {
          map.set(id, { ...saved, status: undefined });
        }
      }
    }
    // A debrief is done once its OA has one, wherever it was written
    for (const [day, items] of Object.entries(frozen)) {
      for (const item of items) {
        const doc = item.kind === "debrief" && item.eventId ? eventDocs[item.eventId] : undefined;
        if ((doc?.debrief || doc?.debriefed) && map.get(item.id)?.status !== "done") map.set(item.id, { item, day, status: "done" });
      }
    }
    return map;
  }, [states, goals, frozen, eventDocs]);

  /** Where the user is in each topic: setup ratings, then practice, stories and debriefs */
  const skills = useMemo<Skills | null>(
    () =>
      settings
        ? skillsFrom(settings.ratings, settings.completedAt, [...skillEvents(results, states), ...debriefEvents(eventDocs)], today)
        : null,
    [settings, results, states, eventDocs, today]
  );

  const pinned = useMemo(
    () => pinnedFrom(states, today).filter(({ item }) => !gone(item)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [states, today, goalStepIds, loaded]
  );

  // Worked out against today, so a new day picks up OAs that went stale or came due
  const ownEvents = useMemo(() => oaEvents(cards, eventDocs, new Date(`${today}T12:00:00`)), [cards, eventDocs, today]);

  // What earlier candidates shared about these OAs' companies (companyDebriefs).
  // Never holds the page up; empty until the Firestore rule allows reading it.
  const [shared, setShared] = useState<CompanyDebrief[]>([]);
  const companyKeys = useMemo(
    () =>
      Array.from(new Set(ownEvents.map((e) => normalizeCompany(cards.find((c) => c.id === e.id)?.company)).filter(Boolean)))
        .sort()
        .slice(0, 10)
        .join("|"),
    [ownEvents, cards]
  );
  useEffect(() => {
    if (!user || !companyKeys) {
      setShared([]);
      return;
    }
    return onSnapshot(
      query(collection(db, COMPANY_DEBRIEFS), where("companyKey", "in", companyKeys.split("|"))),
      (snap) => setShared(snap.docs.map((d) => d.data() as CompanyDebrief).filter((d) => d.by !== user.uid)),
      (e) => {
        console.warn("Could not load what others said about these OAs:", e);
        setShared([]);
      }
    );
  }, [user, companyKeys]);
  const crowds = useMemo(() => {
    const byEvent = new Map<string, Crowd>();
    for (const event of ownEvents) {
      const card = cards.find((c) => c.id === event.id);
      const crowd = card && crowdFor(card, shared);
      if (crowd) byEvent.set(event.id, crowd);
    }
    return byEvent;
  }, [ownEvents, cards, shared]);
  const events = useMemo(
    () =>
      ownEvents.map((e) => {
        const crowd = crowds.get(e.id);
        return crowd ? { ...e, crowd: { people: crowd.people, topics: crowd.topics } } : e;
      }),
    [ownEvents, crowds]
  );

  const plan = useMemo(() => {
    if (!settings || !problems || !loaded) return null;
    return buildPlan({
      today,
      horizon: HORIZON,
      settings,
      skills: skills!,
      pace: paceFrom(results),
      problems,
      practice: practiceLog(results),
      frozen: Object.fromEntries(Object.entries(frozen).filter(([day]) => day <= today)),
      pinned,
      events,
      goals: goals.map(toPlanGoal),
      setAside: states
        .filter((s) => s.status === "skipped" || s.status === "later")
        .map((s) => ({ id: s.item.id, back: s.status === "later" })),
      done: states.filter((s) => s.status === "done").map((s) => s.item.id),
    });
  }, [settings, skills, problems, loaded, today, results, states, frozen, pinned, goals, events]);

  const itemsOn = (day: string): PlanItem[] =>
    (day >= today ? plan?.[day] ?? [] : shownDay(day, frozen, pinned)).filter((item) => !gone(item));

  const plannedDay = useMemo(() => {
    const days = new Map<string, string>();
    for (const [day, items] of Object.entries(plan ?? {})) {
      for (const item of items) if (item.kind === "goal" && !days.has(item.id)) days.set(item.id, day);
    }
    return days;
  }, [plan]);

  // Today is frozen the first time it's worked out, so its list doesn't change under the user
  const todayItems = plan?.[today];
  useEffect(() => {
    if (!user || !todayItems || frozen[today]) return;
    setDoc(doc(db, "users", user.uid, "prepDays", today), { items: plain(todayItems), at: serverTimestamp() }).catch((e) =>
      console.error("Could not save today's prep:", e)
    );
  }, [user, todayItems, frozen, today]);

  return { today, loaded, settings, problems, results, states, stateById, goals, plan, itemsOn, plannedDay, cards, eventDocs, events, skills, crowds };
}
